import { useState } from 'react';
import { clearSavedCache } from '@/lib/persist.ts';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.ts';
import { keys, useMe, useHomeProfiles } from '@/lib/queries.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { Modal } from '@/ui/Modal.tsx';
import { BufferPicker } from '@/theme/BufferPicker.tsx';
import { ThemePicker } from '@/theme/ThemePicker.tsx';
import { BUFFER_PROFILES, useBuffer } from '@/stores/buffer.ts';
import { THEMES } from '@/theme/themes.ts';
import { useTheme } from '@/theme/useTheme.ts';
import { ProfilePicker } from './ProfilePicker.tsx';

/**
 * Who is signed in, the settings, and the way out.
 *
 * ONE PANE AT A TIME, and that is the whole design.
 *
 * The first version listed everything at once — close, three buffer options,
 * four themes, sign out. Ten full-width rows, which is taller than a 720p
 * television, so it scrolled, got cut off at both ends, and the eye had to
 * work out which of two yellow bars in two different groups meant what. A
 * dialog that does not fit is a dialog nobody reads.
 *
 * So the front pane says what each setting IS — "Buffer · Balanced" — and
 * opening one replaces the pane rather than growing it. Four rows, always,
 * whatever gets added later. This is how every set-top box does settings, for
 * the same reason: the screen is far away and the remote has four directions.
 */
export function AccountMenu() {
  const { data } = useMe();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pane, setPane] = useState<'main' | 'profile' | 'buffer' | 'theme' | 'signout'>('main');
  const [busy, setBusy] = useState(false);

  const bufferId = useBuffer((s) => s.id);
  const themeId = useTheme((s) => s.theme);
  const { data: home } = useHomeProfiles();

  /*
   * ONE ROW, ALWAYS THE SAME WORDS.
   *
   * It briefly said "Back to my account" once somebody had switched, which
   * quietly assumed the person holding the remote is the one who signed in.
   * On a television that is exactly the assumption that is wrong: Mom is
   * looking at a screen that offers to take her back to "my account", meaning
   * somebody else's. The account that signed in is simply one more face in
   * the picker, marked as such, and getting to it is the same gesture as
   * getting to any other.
   */
  const canPickProfile = (home?.profiles.length ?? 0) > 0;

  const user = data?.user;
  if (!user) return null;

  const initials = user.displayName.slice(0, 1).toUpperCase();
  const bufferName = BUFFER_PROFILES.find((p) => p.id === bufferId)?.name ?? '';
  const themeName = THEMES.find((t) => t.id === themeId)?.name ?? '';

  const show = (next: typeof pane) => setPane(next);
  const close = () => { setOpen(false); setPane('main'); };

  async function signOut() {
    setBusy(true);
    try {
      await api.logout();
    } catch {
      // The cookie is gone from the server's point of view either way, and
      // refusing to move because the request failed would strand somebody on
      // a screen whose only purpose is leaving.
    }
    // Drop everything, not just `me`: the next person to sign in must not see
    // a frame of the last person's favorites, history or lineup.
    qc.clear();
    clearSavedCache();
    await qc.invalidateQueries({ queryKey: keys.me });
  }

  return (
    <>
      <Focusable
        id="nav-account"
        className="account"
        onSelect={() => { setPane('main'); setOpen(true); }}
      >
        {user.avatarUrl
          ? <img src={user.avatarUrl} alt="" className="account-avatar" />
          : <span className="account-avatar account-initial">{initials}</span>}
      </Focusable>

      {open && (
        <Modal className="account-dialog" onClose={close} labelledBy="account-title">
          <div className="menu-head">
            <div className="account-avatar account-avatar-lg">
              {user.avatarUrl
                ? <img src={user.avatarUrl} alt="" />
                : <span className="account-initial">{initials}</span>}
            </div>
            <div>
              <div className="big" id="account-title">{user.displayName}</div>
              <div className="muted">
                {pane === 'main' ? 'Signed in'
                  : pane === 'signout' ? 'Sign out?'
                  : pane === 'profile' ? 'Who is watching?'
                  : `Choose a ${pane}`}
              </div>
            </div>
          </div>

          <div className="menu-items">
            {pane === 'main' && (
              <>
                {canPickProfile && (
                  <Focusable id="account-profile" className="btn menu-item" onSelect={() => show('profile')}>
                    Switch profile
                  </Focusable>
                )}

                <Focusable id="account-buffer" className="btn menu-item" onSelect={() => show('buffer')}>
                  Buffer <span className="menu-value">{bufferName}</span>
                </Focusable>

                <Focusable id="account-theme" className="btn menu-item" onSelect={() => show('theme')}>
                  Theme <span className="menu-value">{themeName}</span>
                </Focusable>

                <Focusable id="account-signout" className="btn menu-item" onSelect={() => show('signout')}>
                  Sign out
                </Focusable>

                {/* Last. BACK closes this dialog, so the first row belongs to
                    whatever somebody opened it to do. */}
                <Focusable id="account-close" className="btn menu-item" onSelect={close}>
                  Close
                </Focusable>
              </>
            )}

            {pane === 'profile' && (
              <>
                <ProfilePicker onDone={close} />
                <Focusable id="profile-back" className="btn menu-item" onSelect={() => show('main')}>
                  Back
                </Focusable>
              </>
            )}

            {pane === 'buffer' && (
              <>
                <BufferPicker variant="row" onPicked={() => show('main')} />
                <Focusable id="buffer-back" className="btn menu-item" onSelect={() => show('main')}>
                  Back
                </Focusable>
              </>
            )}

            {pane === 'theme' && (
              <>
                <ThemePicker variant="row" onPicked={() => show('main')} />
                <Focusable id="theme-back" className="btn menu-item" onSelect={() => show('main')}>
                  Back
                </Focusable>
              </>
            )}

            {pane === 'signout' && (
              <>
                <div className="menu-note account-warn">
                  Signing out here means signing in again with a code from
                  plex.tv/link. On this television that means finding a phone.
                </div>
                <Focusable id="account-cancel" className="btn menu-item" onSelect={() => show('main')}>
                  Stay signed in
                </Focusable>
                <Focusable
                  id="account-confirm"
                  className="btn menu-item account-danger"
                  onSelect={() => { if (!busy) void signOut(); }}
                >
                  {busy ? 'Signing out…' : 'Yes, sign out'}
                </Focusable>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
